import { Icon } from '@iconify/react';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import config from '../config';
import { toast } from 'react-toastify';
import ImageCropperModal from './common/ImageCropperModal';
import { Modal } from 'react-bootstrap';

const ViewProfileLayer = () => {
    const { user: currentUser, auth, updateUser, logout } = useAuth();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const navigate = useNavigate();

    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(!!sessionId); // Initialize based on URL
    const [error, setError] = useState(null);

    const [imagePreview, setImagePreview] = useState('/assets/images/user-grid/user-grid-img13.png');
    const [imageFile, setImageFile] = useState(null);
    const [coverImagePreview, setCoverImagePreview] = useState('/assets/images/user-grid/user-grid-bg1.png');
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [bioText, setBioText] = useState('');
    const [selectedLanguages, setSelectedLanguages] = useState(['English']);

    // Email change flow: enter new address, then the code sent to it
    const [showEmailChange, setShowEmailChange] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [emailCodeSent, setEmailCodeSent] = useState(false);
    const [emailChangeBusy, setEmailChangeBusy] = useState(false);

    // Image Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        type: null, // 'profile' or 'cover'
        fixedAspectRatio: 1,
        initialAspectRatio: 1,
        minWidth: 750
    });

    const handleCropperHide = () => {
        // Revoke object URL to avoid memory leaks
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        if (cropperState.type === 'profile') {
            setImageFile(croppedFile);
            setImagePreview(previewUrl);
        } else if (cropperState.type === 'cover') {
            setCoverImageFile(croppedFile);
            setCoverImagePreview(previewUrl);
        }
        handleCropperHide();
    };

    // Force Sync Subscription if returning from Stripe
    useEffect(() => {
        const syncSubscription = async () => {
            if (sessionId) {
                console.log('[ViewProfile] Starting subscription sync for session:', sessionId);
                setIsSyncing(true);
                
                const maxRetries = 3;
                let success = false;
                
                for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
                    try {
                        const token = auth.getToken();
                        // Note: Backend expects 'session_id' (underscore) and 'sync=true'
                        const response = await fetch(`${config.API_BASE_URL}/api/payments/subscription/sync`, {
                            method: 'POST',
                            body: JSON.stringify({ session_id: sessionId }),
                            headers: {
                                'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                        });
                        
                        if (!response.ok) {
                            throw new Error(`Sync failed with status: ${response.status}`);
                        }
                        
                        const data = await response.json();
                        
                        // Verify the subscription was actually updated
                        if (data.subscription) {
                            console.log('[ViewProfile] Subscription sync completed. Plan:', data.subscription.plan_name);
                            toast.success('Subscription updated successfully!');
                            success = true;
                            break;
                        } else {
                            throw new Error('No subscription returned from sync');
                        }
                    } catch (err) {
                        console.warn(`[ViewProfile] Sync attempt ${retryCount + 1} failed:`, err.message);
                        
                        if (retryCount < maxRetries - 1) {
                            // Wait before retry (exponential backoff)
                            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                        }
                    }
                }
                
                if (!success) {
                    console.error('[ViewProfile] All sync attempts failed');
                    toast.warning('Payment received! Your subscription will update shortly.');
                }
                
                // Remove session_id from URL to prevent loop/re-sync
                navigate('/view-profile', { replace: true });
                setIsSyncing(false);
            }
        };
        syncSubscription();
    }, [sessionId, auth, navigate]);

    useEffect(() => {
        const fetchUser = async () => {
            // Block user fetch if we are currently syncing subscription
            if (isSyncing) {
                console.log('[ViewProfile] Waiting for subscription sync before fetching user profile...');
                return;
            }

            try {
                setLoading(true);
                const token = auth.getToken();
                const targetId = id || currentUser?.id;

                if (!targetId) {
                    setError('No user ID found');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${config.API_BASE_URL}/api/users/${targetId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `Failed to fetch user profile (${response.status})`);
                }

                const data = await response.json();
                
                // Fetch position details if position_id exists
                let positionTitle = 'N/A';
                if (data.position_id) {
                    try {
                        const posResponse = await fetch(`${config.API_BASE_URL}/api/positions/${data.position_id}`, {
                             headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (posResponse.ok) {
                            const posData = await posResponse.json();
                            positionTitle = posData.title;
                        }
                    } catch (posErr) {
                        console.error('Error fetching position:', posErr);
                    }
                }
                
                setUserData({ ...data, position_title: positionTitle });
                setBioText((data.bio || '').slice(0, 300));
                
                if (data.profile_image) {
                    let imgPath = data.profile_image;
                    if (imgPath.startsWith('http')) {
                        setImagePreview(imgPath);
                    } else if (imgPath.includes('/avatar/')) {
                        setImagePreview(`${config.API_BASE_URL}${imgPath.startsWith('/') ? '' : '/'}${imgPath}`);
                    } else {
                        // Frontend asset
                        if (!imgPath.startsWith('/')) imgPath = '/' + imgPath;
                        setImagePreview(imgPath);
                    }
                } else {
                     // Keep default or set to placeholder
                     setImagePreview('/assets/images/user-grid/user-grid-img13.png');
                }

                if (data.cover_image) {
                    let coverPath = data.cover_image;
                    if (coverPath.startsWith('http')) {
                        setCoverImagePreview(coverPath);
                    } else if (coverPath.includes('/cover/')) {
                         setCoverImagePreview(`${config.API_BASE_URL}${coverPath.startsWith('/') ? '' : '/'}${coverPath}`);
                    } else {
                         if (!coverPath.startsWith('/')) coverPath = '/' + coverPath;
                         setCoverImagePreview(coverPath);
                    }
                } else {
                    setCoverImagePreview('/assets/images/user-grid/user-grid-bg1.png');
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [id, currentUser, isSyncing, auth]);

    const readURL = (input) => {
        if (input.target.files && input.target.files[0]) {
            const file = input.target.files[0];
            
            // Validate file size (10MB limit for source)
            if (file.size > 10 * 1024 * 1024) {
                toast.error("Image max size is 10MB");
                return;
            }
            
            const objectUrl = URL.createObjectURL(file);
            
            setCropperState({
                show: true,
                imageSrc: objectUrl,
                type: 'profile',
                fixedAspectRatio: 1, // 1:1 for profile
                initialAspectRatio: 1,
                minWidth: 400
            });
            
            // Reset input value so same file can be selected again if cancelled
            input.target.value = '';
        }
    };

    const readCoverURL = (input) => {
        if (input.target.files && input.target.files[0]) {
            const file = input.target.files[0];
            
            // Validate file size (10MB limit for source)
            if (file.size > 10 * 1024 * 1024) {
                toast.error("Cover image max size is 10MB");
                return;
            }
            
            const objectUrl = URL.createObjectURL(file);
            
            setCropperState({
                show: true,
                imageSrc: objectUrl,
                type: 'cover',
                fixedAspectRatio: 3/2, // 3:2 for cover (venue header/feed style)
                initialAspectRatio: 3/2,
                minWidth: 750
            });
            
            // Reset input value so same file can be selected again if cancelled
            input.target.value = '';
        }
    };

    const closeEmailChange = () => {
        setShowEmailChange(false);
        setNewEmail('');
        setEmailCode('');
        setEmailCodeSent(false);
    };

    // Uses fetch directly: auth.fetch signs the user out on any 401, including "sign in again first"
    const postEmailChange = async (path, body) => {
        const response = await fetch(`${config.API_BASE_URL}/api/auth/${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
            body: JSON.stringify(body)
        });
        const data = await response.json().catch(() => ({}));
        if (data.error === 'REAUTH_REQUIRED') {
            throw new Error('For security, please sign out, sign back in, and try again within 5 minutes.');
        }
        if (!response.ok) throw new Error(data.message || data.error || 'Could not change your email');
        return data;
    };

    const requestEmailChange = async () => {
        const email = newEmail.trim();
        if (!email || email.toLowerCase() === userData.email.toLowerCase()) {
            toast.error('Enter a different email address');
            return;
        }
        setEmailChangeBusy(true);
        try {
            await postEmailChange('change-email', { email });
            setEmailCodeSent(true);
            toast.success(`Confirmation code sent to ${email}`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setEmailChangeBusy(false);
        }
    };

    const confirmEmailChange = async () => {
        setEmailChangeBusy(true);
        try {
            await postEmailChange('confirm-email-change', { token: emailCode.trim() });
            toast.success('Email updated. Please sign in with your new address.');
            logout();
            navigate('/sign-in');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setEmailChangeBusy(false);
        }
    };

    const updateProfile = async (e) => {
        e.preventDefault();
        console.log("Submitting profile update...");
        setIsSaving(true);

        try {
            const formData = new FormData();
            formData.append('full_name', e.target.elements['name'].value);
            formData.append('phone', e.target.elements['number'].value);
            formData.append('language', selectedLanguages.join(', '));
            formData.append('bio', bioText);

            if (imageFile) {
                formData.append('profile_image', imageFile);
            }
            if (coverImageFile) {
                formData.append('cover_image', coverImageFile);
            }

            const token = auth.getToken();
            console.log(`Sending PUT request to: ${config.API_BASE_URL}/api/users/${userData.id}`);
            
            const response = await fetch(`${config.API_BASE_URL}/api/users/${userData.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || errData.error || 'Failed to update profile');
            }

            const data = await response.json();
            console.log("Profile updated successfully:", data);
            
            setUserData(data.user);
            setBioText((data.user?.bio || '').slice(0, 300));
            if (data.user?.language) {
                const langs = data.user.language.split(',').map(s => s.trim()).filter(Boolean);
                setSelectedLanguages(langs.length > 0 ? langs : ['English']);
            }
            toast.success('Profile updated successfully!');
            setImageFile(null);
            setCoverImageFile(null);
            
            // Optionally refresh the auth user if the current user updated their own profile
            if (currentUser && currentUser.id === userData.id) {
                updateUser(data.user);
            }

        } catch (err) {
            console.error("Profile update error:", err);
            toast.error(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (isSyncing) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Syncing...</span>
                </div>
                <h5 className="text-primary-light">Finalizing subscription update...</h5>
                <p className="text-secondary-light">Please wait while we confirm your payment details.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    if (!userData) return null;

    return (
        <div className="row gy-4">
            {/* Left Side: Read-Only Profile Preview */}
            <div className="col-lg-4">
                <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
                    <div className="px-24 py-16 border-bottom bg-base">
                        <h6 className="text-lg fw-bold text-dark mb-0">Profile Preview</h6>
                    </div>
                    <div className="position-relative">
                        <img
                            src={coverImagePreview}
                            alt="Cover"
                            className="w-100 object-fit-cover"
                            style={{ height: '120px' }}
                        />
                    </div>
                    <div className="pb-24 ms-16 mb-24 me-16 mt--100 position-relative z-1">
                        <div className="text-center border border-top-0 border-start-0 border-end-0 pb-16">
                            <img
                                src={imagePreview}
                                alt=""
                                className="border br-white border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover bg-white shadow-sm"
                            />
                            <h6 className="mb-0 mt-16">{userData.full_name}</h6>
                            <span className="text-secondary-light mb-16">{userData.email}</span>
                        </div>
                        <div className="mt-24">
                            <h6 className="text-lg fw-bold text-dark mb-16">Personal Info</h6>
                            <ul className="list-unstyled mb-0">
                                <li className="d-flex align-items-start gap-2 mb-12">
                                    <span className="w-35 text-sm fw-semibold text-primary-light">
                                        Full Name
                                    </span>
                                    <span className="w-65 text-sm text-secondary-light fw-medium">
                                        {userData.full_name}
                                    </span>
                                </li>
                                <li className="d-flex align-items-start gap-2 mb-12">
                                    <span className="w-35 text-sm fw-semibold text-primary-light">
                                        Email
                                    </span>
                                    <span className="w-65 text-sm text-secondary-light fw-medium text-break">
                                        {userData.email}
                                    </span>
                                </li>
                                <li className="d-flex align-items-start gap-2 mb-12">
                                    <span className="w-35 text-sm fw-semibold text-primary-light">
                                        Phone
                                    </span>
                                    <span className="w-65 text-sm text-secondary-light fw-medium">
                                        {userData.phone_number || 'N/A'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-start gap-2 mb-12">
                                    <span className="w-35 text-sm fw-semibold text-primary-light">
                                        Languages Spoken
                                    </span>
                                    <span className="w-65 text-sm text-secondary-light fw-medium">
                                        {selectedLanguages.length > 0 ? selectedLanguages.join(', ') : (userData.language || 'English')}
                                    </span>
                                </li>
                                <li className="d-flex align-items-start gap-2">
                                    <span className="w-35 text-sm fw-semibold text-primary-light">
                                        Bio
                                    </span>
                                    <span className="w-65 text-sm text-secondary-light fw-medium">
                                        {bioText || userData.bio || 'No bio available.'}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Profile Settings */}
            <div className="col-lg-8">
                <div className="card h-100 border radius-16 overflow-hidden">
                    <div className="px-24 py-16 border-bottom bg-base">
                        <h6 className="text-lg fw-bold text-dark mb-0">Profile Settings</h6>
                    </div>
                    <div className="card-body p-24">
                        {/* Profile Image & Cover Image Upload Controls */}
                        <div className="row g-24 mb-24">
                            <div className="col-md-5">
                                <h6 className="text-sm fw-semibold text-primary-light mb-12">Profile Image</h6>
                                <div className="avatar-upload">
                                    <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                                        <input
                                            type="file"
                                            id="imageUpload"
                                            accept=".png, .jpg, .jpeg"
                                            hidden
                                            onChange={readURL}
                                        />
                                        <label
                                            htmlFor="imageUpload"
                                            className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle cursor-pointer"
                                        >
                                            <Icon icon="solar:camera-outline" className="icon" />
                                        </label>
                                    </div>
                                    <div className="avatar-preview bg-white rounded-circle shadow-sm">
                                        <div
                                            id="imagePreview"
                                            className="bg-white rounded-circle"
                                            style={{
                                                backgroundImage: `url(${imagePreview})`,
                                                backgroundColor: '#ffffff'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-7">
                                <h6 className="text-sm fw-semibold text-primary-light mb-12">Background / Cover Image</h6>
                                <div className="position-relative radius-12 overflow-hidden border" style={{ height: '120px' }}>
                                    <img
                                        src={coverImagePreview}
                                        alt="Cover preview"
                                        className="w-100 h-100 object-fit-cover"
                                    />
                                    <label
                                        htmlFor="coverImageUpload"
                                        className="position-absolute bottom-0 end-0 m-12 hs-btn-secondary hs-btn-sm py-6 px-12 cursor-pointer d-inline-flex align-items-center gap-1 shadow-xs"
                                        style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}
                                    >
                                        <Icon icon="solar:camera-outline" className="text-primary-600 text-base" />
                                        <span className="text-xs fw-semibold text-dark">Change Cover</span>
                                    </label>
                                    <input
                                        type="file"
                                        id="coverImageUpload"
                                        accept=".png, .jpg, .jpeg"
                                        hidden
                                        onChange={readCoverURL}
                                    />
                                </div>
                            </div>
                        </div>

                        <form onSubmit={updateProfile}>
                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="mb-20">
                                        <label
                                            htmlFor="name"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Full Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="name"
                                            defaultValue={userData.full_name}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="mb-20">
                                        <label
                                            htmlFor="email"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Email <span className="text-danger-600">*</span>
                                        </label>
                                        <div className="d-flex gap-2">
                                            <input
                                                type="email"
                                                className="form-control radius-8"
                                                id="email"
                                                value={userData.email || ''}
                                                readOnly
                                            />
                                            {currentUser?.id === userData.id && (
                                                <button
                                                    type="button"
                                                    className="hs-btn-secondary"
                                                    onClick={() => setShowEmailChange(true)}
                                                >
                                                    Change
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="mb-20">
                                        <label
                                            htmlFor="number"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Phone
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="number"
                                            name="phone"
                                            defaultValue={userData.phone_number}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="mb-20">
                                        <label
                                            className="form-label fw-semibold text-primary-light text-sm mb-8 d-block"
                                        >
                                            Languages Spoken <span className="text-danger-600">*</span>
                                        </label>
                                        <div className="d-flex flex-wrap gap-1.5 mb-2">
                                            {['English', 'Arabic', 'French', 'Russian', 'Hindi', 'Spanish', 'German', 'Urdu'].map(lang => {
                                                const isSel = selectedLanguages.includes(lang);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={lang}
                                                        className={`btn btn-sm radius-pill px-2.5 py-1 text-xs fw-medium ${
                                                            isSel ? 'text-white' : 'bg-light text-secondary border'
                                                        }`}
                                                        style={isSel ? { backgroundColor: '#0388d6', border: 'none' } : {}}
                                                        onClick={() => {
                                                            setSelectedLanguages(prev => 
                                                                prev.includes(lang) 
                                                                    ? (prev.length > 1 ? prev.filter(l => l !== lang) : prev) 
                                                                    : [...prev, lang]
                                                            );
                                                        }}
                                                    >
                                                        {isSel && <Icon icon="solar:check-circle-bold" className="me-1" />}
                                                        {lang}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="mb-24">
                                        <label
                                            htmlFor="desc"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Bio
                                        </label>
                                        <div className="hs-field-with-counter">
                                            <textarea
                                                name="desc"
                                                className="form-control radius-8"
                                                id="desc"
                                                rows={4}
                                                maxLength={300}
                                                value={bioText}
                                                onChange={(e) => setBioText(e.target.value.slice(0, 300))}
                                            />
                                            <span className="hs-char-counter">{bioText.length}/300</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center justify-content-end gap-3">
                                    <button
                                        type="button"
                                        className="hs-btn-secondary px-32 py-10 radius-8"
                                        onClick={() => {
                                            setBioText((userData.bio || '').slice(0, 300));
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="hs-btn-primary px-32 py-10 radius-8"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            
            <Modal show={showEmailChange} onHide={closeEmailChange} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold text-dark">Change Email</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label htmlFor="newEmail" className="form-label fw-semibold text-sm mb-8">New email address</label>
                    <input
                        id="newEmail"
                        type="email"
                        className="form-control radius-8"
                        value={newEmail}
                        disabled={emailCodeSent}
                        onChange={(e) => setNewEmail(e.target.value)}
                    />
                    {emailCodeSent && (
                        <div className="mt-16">
                            <label htmlFor="emailCode" className="form-label fw-semibold text-sm mb-8">Confirmation code</label>
                            <input
                                id="emailCode"
                                type="text"
                                className="form-control radius-8"
                                value={emailCode}
                                onChange={(e) => setEmailCode(e.target.value)}
                            />
                            <span className="text-xs text-secondary-light d-block mt-8">
                                We sent a code to the new address. It expires in 15 minutes. Your email stays the same until you confirm, and you will need to sign in again afterwards.
                            </span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="hs-btn-secondary" onClick={closeEmailChange}>Cancel</button>
                    {emailCodeSent ? (
                        <button type="button" className="hs-btn-primary" disabled={emailChangeBusy || !emailCode.trim()} onClick={confirmEmailChange}>
                            Confirm Change
                        </button>
                    ) : (
                        <button type="button" className="hs-btn-primary" disabled={emailChangeBusy} onClick={requestEmailChange}>
                            Send Code
                        </button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Image Cropper Modal */}
            {cropperState.show && (
                <ImageCropperModal
                    show={cropperState.show}
                    onHide={handleCropperHide}
                    imageSrc={cropperState.imageSrc}
                    onCropComplete={handleCropComplete}
                    fixedAspectRatio={cropperState.fixedAspectRatio}
                    initialAspectRatio={cropperState.initialAspectRatio}
                    minWidth={cropperState.minWidth}
                />
            )}
        </div>
    );
};

export default ViewProfileLayer;
